import { ref, watch } from "vue";
import { useRoute } from "vue-router";

interface FeatureFlagObject {
  enabled: boolean;
  // Which apps to ignore this feature flag and treat it as enabled.
  overrideAppIds: string[];
}

type FeatureFlagName =
  | "campaignPurchaseLimit"
  | "buyAll"
  | "addOfferToARunningAB"
  | "campaignsRevamp"
  | "offerMultiLinkABTest"
  | "partialPublish";
type FeatureFlags = Record<FeatureFlagName, FeatureFlagObject>;

const flags = ref<FeatureFlags>({
  campaignsRevamp: {
    enabled: false,
    overrideAppIds: [],
  },
  // Offers × A/B tests multi-link UI: the offer-item ABTest tab, the Included/Excluded cohort pill (in
  // place of the "AB | Status" pill), and the grouped read-only cohort offers display.
  offerMultiLinkABTest: {
    enabled: false,
    overrideAppIds: [
      // VoodooTune Test App
      "02095f71-26f8-4cc7-9d1b-c8cb6eeed342",
      // MobControl
      "877e6e50-3ee3-4174-bc61-62f31ddb587b",
      // Snacky Dash (Juiced Up)
      "aaf32094-73b8-4068-a266-3bec498515e8",
      // Marble Sort
      "544420cc-617e-47a1-ab96-1ca6f4037872",
    ],
  },
  campaignPurchaseLimit: {
    enabled: false,
    overrideAppIds: [
      // Handy Craft
      "85d0179c-4108-4a69-a4d8-6eab15dd4e3b",
      // Voodoo Live
      "26281ef1-3bb2-45c2-90c5-3f3907cadb6e",
      // VoodooTune Test App
      "02095f71-26f8-4cc7-9d1b-c8cb6eeed342",
      // MobControl
      "877e6e50-3ee3-4174-bc61-62f31ddb587b",
    ],
  },
  buyAll: {
    enabled: false,
    overrideAppIds: [
      // Handy Craft
      "85d0179c-4108-4a69-a4d8-6eab15dd4e3b",
      // Voodoo Live
      "26281ef1-3bb2-45c2-90c5-3f3907cadb6e",
      // Monster Survivor
      // "139848e4-9e9f-4180-a62b-09c6ad933119",
      // VoodooTune Test App
      "02095f71-26f8-4cc7-9d1b-c8cb6eeed342",
      // Card Factory
      "a3af8f0e-bedc-462c-8d87-34a98b11cf87",
      // Mob Control,
      "877e6e50-3ee3-4174-bc61-62f31ddb587b",
      // Sand Loop,
      "b4d114ef-041b-444b-82f8-50a4e73f1808",
      // Cup Heroes
      "9e27177f-5873-4a82-9c68-90a65ef3d45d",
      // Marble Sort
      "544420cc-617e-47a1-ab96-1ca6f4037872",
      // Block Pals
      "011427d4-6224-415f-9f8a-c9dd2bd4224f",
      // Color Bus Trip
      "0a6d4d93-0776-4840-931d-581ba5656134",
      // Snacky Dash (Juiced Up)
      "aaf32094-73b8-4068-a266-3bec498515e8",
    ],
  },
  partialPublish: {
    enabled: true,
    overrideAppIds: [
      "85d0179c-4108-4a69-a4d8-6eab15dd4e3b", // Handy Craft
      "02095f71-26f8-4cc7-9d1b-c8cb6eeed342", // VoodooTune Test App
      "ee7dcbfc-bf66-4fdf-9ba4-88e53216aad", // Ball Mayhem!
      "9e7a6c53-31d3-4e70-baad-8d0704f37cc4", // Bucket Crusher
      "6c5d0e1b-32bc-40bc-a9da-42e60012a904", // Castle Raid!
      "b8f65ea5-c635-43ef-8fdc-ce711f1a16ff", // Cinema Tycoon
      "4abb35b9-3622-4e91-b2c7-e01f816af5f9", // Color Road
      "e9225bae-7414-47bd-8bd7-66a527c7818a", // Destiny Run
      "93050de7-4c35-47a5-9ef5-8f1f4052007d", // Draw Climber
      "ae6e0428-c075-451f-8dc4-074806549da0", // Dribble Hoops
      "4e83ffa8-9de4-469e-95e3-8b47bbb23221", // Fail Run
      "cafbc800-bcd0-47c3-a64a-3acc81f012cc", // Flying Arrow!
      "b1086673-2c36-43d4-a863-16df77bddedb", // Go Plane
      "eff59032-515f-49da-bdf9-0c3402163af7", // Good Slice
      "19bf3e92-f4f7-41d0-8a99-04d18a0a9122", // Jump Dunk 3D
      "3c10752d-cf43-4686-aca8-133f758b7890", // Merge Grabber
      "18da09c4-ddcf-428e-b54b-634bc0fc9a5c", // Outdo.io
      "67e7dc53-f8bd-41d6-b5f7-9b8956947a1f", // Paper 4
      "630d28d7-51b6-4ea7-9eb7-269e4e10548c", // Paper 3
      "3b7ee443-309a-408c-b5e0-8643ed813b42", // Planet Bomber
      "dce91ad5-048e-4144-bc26-ef20b63060d0", // Roof Rails
      "98a70b11-b986-4859-a74e-bfe6fd7b8893", // Rope and Demolish
      "471a8733-7045-4299-8d91-a69019bd0349", // Run Healthy
      "c5171712-dd3d-4bda-aad7-e60df195d23f", // Slap and Run
      "b6c18231-3e74-432e-af9c-b5ca58094628", // Slice It All
      "62fe52c7-38b3-47d0-8863-88abc3595180", // Stack Colors
      "658a328a-deab-474f-9a8c-9efb1b4276a0", // Aircraft Masters
      "877e6e50-3ee3-4174-bc61-62f31ddb587b", // Mob Control
    ],
  },
  addOfferToARunningAB: {
    enabled: false,
    overrideAppIds: [
      // Handy Craft
      "85d0179c-4108-4a69-a4d8-6eab15dd4e3b",
      // VoodooTune Test App
      "02095f71-26f8-4cc7-9d1b-c8cb6eeed342",
      // MobControl
      "877e6e50-3ee3-4174-bc61-62f31ddb587b",
      // Monster survivor
      "139848e4-9e9f-4180-a62b-09c6ad933119",
      // Card Factory
      "a3af8f0e-bedc-462c-8d87-34a98b11cf87",
      // Marble Sort
      "544420cc-617e-47a1-ab96-1ca6f4037872",
      // This is Blast
      "30b7ed2c-5212-4e98-98e5-9d774c730f74",
      // Sand Loop
      "b4d114ef-041b-444b-82f8-50a4e73f1808",
      // Juiced up
      "aaf32094-73b8-4068-a266-3bec498515e8",
    ],
  },
});

export default function useFeatureFlags(gameId: string = "") {
  const route = useRoute();

  const checkQueryParams = (key: string) => {
    const featureFlag = flags.value[key as FeatureFlagName];
    if (featureFlag) {
      featureFlag.enabled = featureFlag.enabled || route.query[key] === "true";
    }
  };

  const checkGameId = (key: string) => {
    const featureFlag = flags.value[key as FeatureFlagName];
    if (featureFlag) {
      featureFlag.enabled = featureFlag.enabled || featureFlag?.overrideAppIds?.includes(gameId);
    }
  };

  watch(
    () => route.query,
    () => {
      if (Object.keys(route.query).length > 0) {
        // Check the page when manually changing the query params
        Object.keys(route.query).forEach(checkQueryParams);
      } else {
        // Check the page when navigating to a new page
        Object.keys(flags.value).forEach(checkGameId);
      }
    },
    { immediate: true }
  );

  return {
    flags,
  };
}
